import { INSIGHTS_TOPICS, readingTime, wordCount } from '../data/insights';
import { SITE_URL, SITE_NAME, LOGO_URL, absUrl, breadcrumbJsonLd, PAGE_SEO } from './site';

export const stripMarkup = (text) => (text || '').replace(/\*\*/g, '');

export const truncate = (text, max = 155) => {
  const clean = stripMarkup(text).replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const sentence = cut.lastIndexOf('. ');
  if (sentence > 80) return cut.slice(0, sentence + 1);
  const comma = cut.lastIndexOf(', ');
  const end = comma > 100 ? comma : cut.lastIndexOf(' ');
  return `${cut.slice(0, end).replace(/[,.;:\s]+$/, '')} …`;
};

export const articlePath = (topic, article) => (article ? `/insights/${topic.slug}/${article.slug}` : `/insights/${topic.slug}`);

export const ogImagePath = (topic, article) => (article ? `/insights/og/${topic.slug}--${article.slug}.jpg` : `/insights/og/${topic.slug}.jpg`);

export const articleTitle = (article) => `${article.title} | ${SITE_NAME}`;

export const articleDescription = (topic, article, isMain) => (isMain ? truncate(article.intro) : truncate(article.teaser || article.intro));

const faqJsonLd = (article, url) => {
  const items = article.sections.flatMap((s) => s.blocks.filter((b) => b.type === 'faq').flatMap((b) => b.items));
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: stripMarkup(it.q),
      acceptedAnswer: { '@type': 'Answer', text: stripMarkup(it.a) },
    })),
  };
};

export const articleJsonLd = (topic, article, isMain) => {
  const path = articlePath(topic, isMain ? null : article);
  const url = absUrl(path);
  const image = absUrl(ogImagePath(topic, isMain ? null : article));
  const crumbs = [
    { name: 'Startseite', path: '/' },
    { name: 'Insights', path: '/insights' },
    { name: topic.title, path: `/insights/${topic.slug}` },
  ];
  if (!isMain) crumbs.push({ name: article.shortTitle || article.title, path });

  const list = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${url}#article`,
      headline: article.title,
      description: articleDescription(topic, article, isMain),
      image: [image, topic.image],
      datePublished: topic.datePublished,
      dateModified: topic.dateModified,
      inLanguage: 'de-DE',
      wordCount: wordCount(article),
      timeRequired: `PT${readingTime(article)}M`,
      articleSection: topic.title,
      keywords: [topic.title, 'Gastronomie', 'Digitalisierung', ...(article.keywords || [])].join(', '),
      author: { '@type': 'Organization', name: 'Gastro-Beratung.de', url: SITE_URL },
      publisher: { '@type': 'Organization', name: 'Gastro-Beratung.de', logo: { '@type': 'ImageObject', url: LOGO_URL } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      isPartOf: isMain ? { '@type': 'CollectionPage', '@id': absUrl('/insights') } : { '@type': 'WebPage', '@id': absUrl(`/insights/${topic.slug}`) },
      ...(isMain && topic.subArticles.length
        ? { hasPart: topic.subArticles.map((a) => ({ '@type': 'Article', headline: a.title, url: absUrl(articlePath(topic, a)) })) }
        : {}),
    },
    breadcrumbJsonLd(crumbs),
  ];
  const faq = faqJsonLd(article, url);
  if (faq) list.push(faq);
  return list;
};

export const articleSeo = (topic, article, isMain) => ({
  title: articleTitle(article),
  description: articleDescription(topic, article, isMain),
  path: articlePath(topic, isMain ? null : article),
  image: absUrl(ogImagePath(topic, isMain ? null : article)),
  type: 'article',
  publishedTime: topic.datePublished,
  modifiedTime: topic.dateModified,
  section: topic.title,
  jsonLd: articleJsonLd(topic, article, isMain),
});

export const insightsOverviewSeo = () => ({
  ...PAGE_SEO.insights,
  image: absUrl('/insights/og/_overview.jpg'),
  jsonLd: [
    breadcrumbJsonLd([{ name: 'Startseite', path: '/' }, { name: 'Insights', path: '/insights' }]),
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': absUrl('/insights'),
      name: 'Insights – Wissen zur Digitalisierung in der Gastronomie',
      description: PAGE_SEO.insights.description,
      inLanguage: 'de-DE',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: INSIGHTS_TOPICS.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: absUrl(`/insights/${t.slug}`),
          name: t.mainArticle.title,
        })),
      },
    },
  ],
});

export const allInsightsRoutes = () =>
  INSIGHTS_TOPICS.flatMap((topic) => [
    { topic, article: topic.mainArticle, isMain: true, path: articlePath(topic, null) },
    ...topic.subArticles.map((article) => ({ topic, article, isMain: false, path: articlePath(topic, article) })),
  ]);
